class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('change', this.onVariantChange.bind(this), true);
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  async onVariantChange(event) {
    const select = event.target.closest('[data-cart-variant-option]');
    if (!select) return;

    // CartItems treats every bubbling change as a quantity update. Variant
    // controls are handled here instead.
    event.stopPropagation();

    const cartItem = select.closest('.cart-item');
    const variantDataElement = cartItem?.querySelector('[data-cart-product-variants]');
    if (!cartItem || !variantDataElement) return;

    const optionSelects = [...cartItem.querySelectorAll('[data-cart-variant-option]')];
    let variants;

    try {
      variants = JSON.parse(variantDataElement.textContent);
    } catch (error) {
      this.showVariantError(cartItem, window.cartStrings?.error || 'Unable to update this item.');
      this.resetVariantSelects(optionSelects);
      return;
    }

    const currentVariant = variants.find(
      (variant) => String(variant.id) === String(cartItem.dataset.cartVariantId),
    );
    const selectedOptions = [...(currentVariant?.options || [])];

    optionSelects.forEach((optionSelect) => {
      selectedOptions[Number(optionSelect.dataset.optionPosition)] = optionSelect.value;
    });

    const replacementVariant = variants.find(
      (variant) =>
        variant.options?.length === selectedOptions.length &&
        variant.options.every((value, index) => value === selectedOptions[index]),
    );

    if (!replacementVariant?.available) {
      const optionName = select.getAttribute('aria-label')?.trim().toLowerCase() || 'variant';
      this.showVariantError(cartItem, `This ${optionName} is sold out`);
      this.resetVariantSelects(optionSelects);
      return;
    }

    if (String(replacementVariant.id) === cartItem.dataset.cartVariantId) return;

    this.setVariantLoading(cartItem, true, select);
    this.showVariantError(cartItem, '');

    try {
      await this.replaceCartVariant(cartItem, replacementVariant.id);
    } catch (error) {
      this.resetVariantSelects(optionSelects);
      this.showVariantError(cartItem, error.message || window.cartStrings?.error || 'Unable to update this item.');
      this.setVariantLoading(cartItem, false, select);
    }
  }

  async replaceCartVariant(cartItem, replacementVariantId) {
    const initialCart = await this.fetchCart();
    const lineKey = cartItem.dataset.cartLineKey;
    const lineIndex = Number(cartItem.dataset.cartLineIndex) - 1;
    const originalLine = initialCart.items.find((item) => item.key === lineKey) || initialCart.items[lineIndex];

    if (!originalLine) throw new Error(window.cartStrings?.error || 'Unable to find this cart item.');

    const matchingReplacementLine = initialCart.items.find(
      (item) =>
        String(item.variant_id) === String(replacementVariantId) &&
        this.sameLineConfiguration(item, originalLine),
    );
    const previousReplacementQuantity = matchingReplacementLine?.quantity || 0;
    const replacementItem = {
      id: replacementVariantId,
      quantity: originalLine.quantity,
    };

    if (originalLine.properties && Object.keys(originalLine.properties).length) {
      replacementItem.properties = originalLine.properties;
    }

    const sellingPlanId = originalLine.selling_plan_allocation?.selling_plan?.id;
    if (sellingPlanId) replacementItem.selling_plan = sellingPlanId;

    await this.cartRequest(routes.cart_add_url, { items: [replacementItem] });

    try {
      const cartAfterAdd = await this.fetchCart();
      const originalLineAfterAdd = cartAfterAdd.items.find(
        (item) =>
          String(item.variant_id) === String(originalLine.variant_id) &&
          this.sameLineConfiguration(item, originalLine),
      );

      if (!originalLineAfterAdd) throw new Error(window.cartStrings?.error || 'Unable to update this cart item.');

      const finalCart = await this.cartRequest(routes.cart_change_url, {
        id: originalLineAfterAdd.key,
        quantity: 0,
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      });

      this.renderContents(finalCart);
    } catch (error) {
      await this.rollbackVariantAdd(replacementVariantId, originalLine, previousReplacementQuantity);
      throw error;
    }
  }

  async rollbackVariantAdd(replacementVariantId, originalLine, previousQuantity) {
    try {
      const cart = await this.fetchCart();
      const replacementLine = cart.items.find(
        (item) =>
          String(item.variant_id) === String(replacementVariantId) &&
          this.sameLineConfiguration(item, originalLine),
      );
      if (!replacementLine) return;

      await this.cartRequest(routes.cart_change_url, {
        id: replacementLine.key,
        quantity: previousQuantity,
      });
    } catch (rollbackError) {
      console.error('Unable to roll back cart variant change.', rollbackError);
    }
  }

  sameLineConfiguration(firstLine, secondLine) {
    const normalizeProperties = (properties = {}) =>
      JSON.stringify(Object.entries(properties).sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey)));
    const firstPlan = firstLine.selling_plan_allocation?.selling_plan?.id || null;
    const secondPlan = secondLine.selling_plan_allocation?.selling_plan?.id || null;

    return (
      normalizeProperties(firstLine.properties) === normalizeProperties(secondLine.properties) &&
      firstPlan === secondPlan
    );
  }

  async fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(window.cartStrings?.error || 'Unable to load the cart.');
    return response.json();
  }

  async cartRequest(url, body) {
    const response = await fetch(url, {
      ...fetchConfig(),
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (!response.ok || data.status || data.errors) {
      throw new Error(
        data.description || data.message || data.errors || window.cartStrings?.error || 'Unable to update the cart.',
      );
    }

    return data;
  }

  resetVariantSelects(selects) {
    selects.forEach((select) => {
      select.value = select.dataset.selectedValue;
      this.syncVariantLabel(select);
    });
  }

  syncVariantLabel(select) {
    const label = select.closest('.fourfourfour-cart-item-variant-select-wrapper')?.querySelector(
      '[data-cart-variant-selected-value]',
    );
    if (label) label.textContent = select.options[select.selectedIndex]?.text || select.value;
  }

  setVariantLoading(cartItem, isLoading, activeSelect) {
    cartItem.toggleAttribute('aria-busy', isLoading);
    cartItem.querySelectorAll('[data-cart-variant-option]').forEach((select) => {
      select.disabled = isLoading;
    });

    cartItem.querySelectorAll('.fourfourfour-cart-item-variant-select-wrapper').forEach((wrapper) => {
      const isActive = isLoading && wrapper === activeSelect?.closest('.fourfourfour-cart-item-variant-select-wrapper');
      wrapper.classList.toggle('is-loading', isActive);
      wrapper.querySelector('.fourfourfour-cart-variant-spinner')?.classList.toggle('hidden', !isActive);
    });
  }

  showVariantError(cartItem, message) {
    const errorElement = cartItem.querySelector('.cart-item__error-text');
    if (errorElement) errorElement.textContent = message;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
